import { inject, Injectable, signal } from '@angular/core';
import { PageEvent } from '@angular/material/paginator';
import { Sort } from '@angular/material/sort';
import { userApi, userDtos, webApi, webDtos } from '@vectronic/openApi-admin';
import {
  BehaviorSubject,
  catchError,
  forkJoin,
  of,
  shareReplay,
  startWith,
  Subscription,
  switchMap,
  zip,
} from 'rxjs';
import { tap } from 'rxjs/operators';
import { GroupItem, ItemHelp, MemberItem } from '../genericHelp/item.help';
import { isTrapId } from '../helpers/device-id.helpers';

@Injectable({ providedIn: 'root' })
export class UserAccountService {
  private userApi = inject(userApi.All);
  private webApiUsers = inject(webApi.AdminUsers);

  selectedDeviceIds = signal<number[]>(undefined);

  userAccountsWithAdditionalData$ = new BehaviorSubject<webDtos.UserAccountAdmin[]>([]);
  userAccountsWithAdditionalDataCount$ = new BehaviorSubject<number>(undefined);
  emailFilter = '';
  page = 0;
  count = 15;
  orderBy = 'id';
  orderDirection = 'desc';

  private refreshAllUserAccounts$ = new BehaviorSubject<void>(null);

  allUserAccounts$ = this.refreshAllUserAccounts$.pipe(
    switchMap(() =>
      this.userApi.Account.getUsersByIds([]).pipe(
        catchError(() => of([] as userDtos.UserAccount[])),
      ),
    ),
    startWith([] as userDtos.UserAccount[]),
    shareReplay(1),
  );

  allLightUsers$ = this.refreshAllUserAccounts$.pipe(
    switchMap(() => this.userApi.Account.getLightUsers()),
    startWith([] as userDtos.UserAccountLight[]),
    shareReplay(1),
  );

  onPageChanged({ pageIndex, pageSize }: PageEvent) {
    this.count = pageSize;
    this.page = pageIndex;
    this.refresh(false);
  }

  onTableSort({ active, direction }: Sort) {
    const allowedColumns = ['id', 'email', 'name', 'organisation', 'phone', 'insertTs'];
    if (!allowedColumns.includes(active)) return;
    this.orderBy = active;
    this.orderDirection = direction;
    this.refresh(false);
  }

  subscription: Subscription;

  refresh(reloadCount: boolean) {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
    const email = this.emailFilter !== '' ? this.emailFilter.toLowerCase() : undefined;
    const collarIdFilter =
      this.selectedDeviceIds()?.length > 0
        ? this.selectedDeviceIds().filter((id) => !isTrapId(id))
        : undefined;
    const trapIdFilter =
      this.selectedDeviceIds()?.length > 0
        ? this.selectedDeviceIds().filter((id) => isTrapId(id))
        : undefined;

    const filterRequestDto: webDtos.UserAccountRequest = {
      collarIdFilter: collarIdFilter?.length > 0 ? collarIdFilter : undefined,
      trapIdFilter: trapIdFilter?.length > 0 ? trapIdFilter : undefined,
    };
    const users$ = this.webApiUsers.getUsersAsAdmin(
      this.page,
      this.count,
      this.orderBy,
      this.orderDirection,
      filterRequestDto,
      email,
    );

    const doReload = reloadCount || !this.userAccountsWithAdditionalDataCount$.value;
    const count$ = doReload
      ? this.userApi.Account.getUserCount(filterRequestDto, email)
      : of(this.userAccountsWithAdditionalDataCount$.value);

    this.subscription = zip(users$, count$).subscribe(([user, count]) => {
      this.userAccountsWithAdditionalData$.next(user);
      this.userAccountsWithAdditionalDataCount$.next(count);
    });
  }

  getMembersForUser(target: MemberItem, userAccountId: number) {
    //TODO  mabe with <T extends MemberItem> and return as Observable<ToUserDto<T>[]>
    return this.userApi.Items[target].adminGetAllObjectsForUser(userAccountId);
  }

  getGroupsForUser(target: GroupItem, userAccountId: number) {
    const memberTarget = ItemHelp.toMember(target);
    return this.userApi.Items[memberTarget].adminGetAllGroupsForUser(userAccountId);
  }

  setIgnoreNotificationLimitForUser(userAccountId: number, ignoreNotificationLimit: boolean) {
    return this.userApi.Account.adminSetIgnoreNotificationLimitForUser(
      userAccountId,
      ignoreNotificationLimit,
    );
  }

  deleteUserAccount(userAccountId: number) {
    return this.webApiUsers
      .adminDeleteUserById(userAccountId)
      .pipe(tap(() => this.refreshAllUserAccounts$.next(null)));
  }

  requireUserAccounts(users: { name?: string; email: string }[]) {
    return forkJoin(
      users.map((user) => this.userApi.Account.requireUser(user.email, user.name)),
    ).pipe(tap(() => this.refreshAllUserAccounts$.next(null)));
  }

  setUserName(userAccountId: number, newName: string) {
    return this.userApi.Account.setNameForUser(userAccountId, newName).pipe(
      tap(() => this.refreshAllUserAccounts$.next(null)),
    );
  }
}
